/**
 * SOOHA K-Beauty Theme - Global JavaScript
 *
 * Copyright (c) 2025 JADEV-CORP
 * All rights reserved.
 *
 * Best practices implemented:
 * - Event delegation for performance
 * - Passive event listeners for scroll/touch
 * - RequestAnimationFrame for animations
 * - AbortController for fetch cancellation
 * - Focus trap for modals/drawers
 * - Proper touch event handling for mobile
 * - Error boundaries for async operations
 * - Memory leak prevention
 */

(function() {
  'use strict';

  // ============================================
  // Feature Detection & Configuration
  // ============================================

  const supportsPassive = (() => {
    let result = false;
    try {
      const opts = Object.defineProperty({}, 'passive', {
        get: function() { result = true; return true; }
      });
      window.addEventListener('testPassive', null, opts);
      window.removeEventListener('testPassive', null, opts);
    } catch (e) { /* ignore */ }
    return result;
  })();

  const passiveOption = supportsPassive ? { passive: true } : false;

  const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ============================================
  // Utility Functions
  // ============================================

  /**
   * Debounce function with leading edge option
   */
  function debounce(func, wait, leading = false) {
    let timeout;
    let lastArgs;

    return function executedFunction(...args) {
      lastArgs = args;

      const later = () => {
        timeout = null;
        if (!leading) func.apply(this, lastArgs);
      };

      const callNow = leading && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);

      if (callNow) func.apply(this, lastArgs);
    };
  }

  /**
   * Throttle using requestAnimationFrame
   */
  function throttleRAF(func) {
    let ticking = false;
    let lastArgs;

    return function throttled(...args) {
      lastArgs = args;
      if (!ticking) {
        requestAnimationFrame(() => {
          func.apply(this, lastArgs);
          ticking = false;
        });
        ticking = true;
      }
    };
  }

  /**
   * Format money according to shop settings
   */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    cents = parseInt(cents, 10);
    if (isNaN(cents)) return '';

    const value = cents / 100;
    const formatString = format || window.sooha?.moneyFormat || '{{amount}} EUR';

    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0';
      const fixedNumber = Number(number).toFixed(precision);
      const parts = fixedNumber.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      const centsStr = parts[1] ? decimal + parts[1] : '';
      return dollars + centsStr;
    };

    let formattedValue = '';
    const match = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return formatWithDelimiters(value, 2);

    switch (match[1]) {
      case 'amount':
        formattedValue = formatWithDelimiters(value, 2);
        break;
      case 'amount_no_decimals':
        formattedValue = formatWithDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        formattedValue = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formattedValue = formatWithDelimiters(value, 0, '.', ',');
        break;
      default:
        formattedValue = formatWithDelimiters(value, 2);
    }

    return formatString.replace(/\{\{\s*\w+\s*\}\}/, formattedValue);
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Get all focusable elements within a container
   */
  function getFocusableElements(container) {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    return Array.from(container.querySelectorAll(focusableSelectors))
      .filter(el => el.offsetParent !== null);
  }

  /**
   * Create a focus trap for modal/drawer accessibility
   */
  function createFocusTrap(container) {
    let firstFocusable = null;
    let lastFocusable = null;

    function updateFocusableElements() {
      const focusable = getFocusableElements(container);
      firstFocusable = focusable[0];
      lastFocusable = focusable[focusable.length - 1];
    }

    function handleKeydown(e) {
      if (e.key !== 'Tab') return;

      updateFocusableElements();
      if (!firstFocusable || !lastFocusable) return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    }

    return {
      activate() {
        updateFocusableElements();
        container.addEventListener('keydown', handleKeydown);
        if (firstFocusable) {
          setTimeout(() => firstFocusable.focus(), 100);
        }
      },
      deactivate() {
        container.removeEventListener('keydown', handleKeydown);
      }
    };
  }

  // Expose utilities to global
  window.Shopify = window.Shopify || {};
  window.Shopify.formatMoney = formatMoney;
  window.SOOHA = window.SOOHA || {};
  window.SOOHA.utils = { debounce, throttleRAF, formatMoney, createFocusTrap, escapeHtml };

  // ============================================
  // Error Notification System
  // ============================================

  /**
   * Show user-friendly error notification
   * Follows Shopify UX best practices
   */
  function showCartError(message, type = 'generic') {
    // Remove any existing error toast
    const existingToast = document.querySelector('.cart-error-toast');
    if (existingToast) {
      existingToast.remove();
    }

    // Create toast
    const toast = document.createElement('div');
    toast.className = `cart-error-toast cart-error-toast--${type}`;
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    
    // Icon based on error type
    let icon = '⚠️';
    if (type === 'out_of_stock') icon = '📦';
    if (type === 'insufficient_stock') icon = '📉';
    if (type === 'cart_limit') icon = '🛒';
    
    toast.innerHTML = `
      <div class="cart-error-toast__content">
        <span class="cart-error-toast__icon">${icon}</span>
        <span class="cart-error-toast__message">${escapeHtml(message)}</span>
      </div>
      <button class="cart-error-toast__close" aria-label="Close">×</button>
    `;
    
    document.body.appendChild(toast);

    // Add styles if not already present
    if (!document.getElementById('cart-error-toast-styles')) {
      const style = document.createElement('style');
      style.id = 'cart-error-toast-styles';
      style.textContent = `
        .cart-error-toast {
          position: fixed;
          bottom: 24px;
          left: 50%;
          transform: translateX(-50%) translateY(100px);
          background: #121212;
          color: #ffffff;
          padding: 16px 20px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 500;
          z-index: 10001;
          opacity: 0;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          pointer-events: none;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1);
          max-width: 90vw;
          min-width: 300px;
        }
        .cart-error-toast.is-visible {
          opacity: 1;
          transform: translateX(-50%) translateY(0);
          pointer-events: auto;
        }
        .cart-error-toast--out_of_stock {
          background: #dc2626;
        }
        .cart-error-toast--insufficient_stock {
          background: #f59e0b;
        }
        .cart-error-toast--cart_limit {
          background: #3b82f6;
        }
        .cart-error-toast__content {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .cart-error-toast__icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .cart-error-toast__message {
          flex: 1;
          line-height: 1.4;
        }
        .cart-error-toast__close {
          background: none;
          border: none;
          color: white;
          font-size: 24px;
          line-height: 1;
          cursor: pointer;
          padding: 0;
          margin-left: 12px;
          opacity: 0.7;
          transition: opacity 0.2s ease;
        }
        .cart-error-toast__close:hover {
          opacity: 1;
        }
        @media (max-width: 640px) {
          .cart-error-toast {
            bottom: 80px;
            min-width: 280px;
            font-size: 13px;
            padding: 14px 16px;
          }
        }
      `;
      document.head.appendChild(style);
    }

    // Close button handler
    const closeBtn = toast.querySelector('.cart-error-toast__close');
    closeBtn.addEventListener('click', () => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    });

    // Show toast
    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    // Auto-hide after 5 seconds
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    }, 5000);

    // Haptic feedback on mobile
    if ('vibrate' in navigator) {
      navigator.vibrate([50, 100, 50]);
    }
  }

  // ============================================
  // Cart Success Toast
  // ============================================

  function showCartSuccess() {
    // Remove any existing success toast
    const existing = document.querySelector('.cart-success-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'cart-success-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');

    const icon = document.createElement('span');
    icon.className = 'cart-success-toast__icon';
    icon.textContent = '\u2713';

    const msg = document.createElement('span');
    msg.className = 'cart-success-toast__message';
    msg.textContent = document.documentElement.lang === 'fr' ? 'Ajouté au panier' : 'Added to cart';

    toast.appendChild(icon);
    toast.appendChild(msg);
    document.body.appendChild(toast);

    // Inject styles once
    if (!document.getElementById('cart-success-toast-styles')) {
      const style = document.createElement('style');
      style.id = 'cart-success-toast-styles';
      style.textContent = `
        .cart-success-toast {
          position: fixed;
          top: 24px;
          left: 50%;
          transform: translateX(-50%) translateY(-60px);
          background: var(--color-text, #121212);
          color: #fff;
          padding: 12px 24px;
          border-radius: var(--radius-full, 9999px);
          font-size: 14px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
          z-index: 10001;
          opacity: 0;
          transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s ease;
          pointer-events: none;
          box-shadow: 0 8px 32px rgba(0,0,0,0.15);
        }
        .cart-success-toast.is-visible {
          opacity: 1;
          transform: translateX(-50%) translateY(0);
        }
        .cart-success-toast__icon {
          width: 20px;
          height: 20px;
          background: rgba(255,255,255,0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
        }
        @media (max-width: 640px) {
          .cart-success-toast {
            top: auto;
            bottom: 80px;
            font-size: 13px;
            padding: 10px 20px;
          }
        }
      `;
      document.head.appendChild(style);
    }

    requestAnimationFrame(() => toast.classList.add('is-visible'));
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 400);
    }, 2000);
  }

  // ============================================
  // Product Availability Notifications
  // ============================================

  function initProductNotifications() {
    document.addEventListener('click', (e) => {
      const notifyBtn = e.target.closest('[data-notify-product]');
      if (!notifyBtn) return;

      e.preventDefault();
      e.stopPropagation();

      const productHandle = notifyBtn.dataset.notifyProduct;
      showNotifyModal(productHandle, notifyBtn);
    });
  }

  function showNotifyModal(productHandle, triggerBtn) {
    // Create modal
    const modal = document.createElement('div');
    modal.className = 'notify-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', document.documentElement.lang === 'fr' ? 'Notification de disponibilité' : 'Availability notification');
    
    const lang = document.documentElement.lang || 'fr';
    const isFr = lang === 'fr';
    
    modal.innerHTML = `
      <div class="notify-modal__overlay"></div>
      <div class="notify-modal__content">
        <button class="notify-modal__close" aria-label="${isFr ? 'Fermer' : 'Close'}">×</button>
        <div class="notify-modal__icon">🔔</div>
        <h3 class="notify-modal__title">${isFr ? 'Être notifié' : 'Get Notified'}</h3>
        <p class="notify-modal__description">
          ${isFr ? 'Recevez un email dès que ce produit est de nouveau disponible.' : 'Receive an email as soon as this product is back in stock.'}
        </p>
        <form class="notify-modal__form" data-notify-form>
          <input 
            type="email" 
            class="notify-modal__input" 
            placeholder="${isFr ? 'votre@email.com' : 'your@email.com'}"
            required
            data-notify-email
          >
          <button type="submit" class="notify-modal__submit">
            ${isFr ? 'Me notifier' : 'Notify Me'}
          </button>
        </form>
        <p class="notify-modal__privacy">
          ${isFr ? 'Nous respectons votre vie privée. Aucun spam.' : 'We respect your privacy. No spam.'}
        </p>
      </div>
    `;
    
    document.body.appendChild(modal);

    // Add styles if not present
    if (!document.getElementById('notify-modal-styles')) {
      const style = document.createElement('style');
      style.id = 'notify-modal-styles';
      style.textContent = `
        .notify-modal {
          position: fixed;
          inset: 0;
          z-index: 10002;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease;
        }
        .notify-modal.is-open {
          opacity: 1;
          pointer-events: auto;
        }
        .notify-modal__overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(4px);
        }
        .notify-modal__content {
          position: relative;
          width: min(420px, 90vw);
          background: white;
          border-radius: 20px;
          padding: 32px 24px;
          text-align: center;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          transform: scale(0.9);
          transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .notify-modal.is-open .notify-modal__content {
          transform: scale(1);
        }
        .notify-modal__close {
          position: absolute;
          top: 16px;
          right: 16px;
          width: 32px;
          height: 32px;
          border: none;
          background: rgba(0, 0, 0, 0.05);
          border-radius: 50%;
          font-size: 24px;
          line-height: 1;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .notify-modal__close:hover {
          background: rgba(0, 0, 0, 0.1);
          transform: rotate(90deg);
        }
        .notify-modal__icon {
          font-size: 48px;
          margin-bottom: 16px;
        }
        .notify-modal__title {
          font-size: 24px;
          font-weight: 600;
          margin-bottom: 12px;
          color: #121212;
        }
        .notify-modal__description {
          font-size: 14px;
          color: #6b7280;
          margin-bottom: 24px;
          line-height: 1.5;
        }
        .notify-modal__form {
          display: flex;
          gap: 8px;
          margin-bottom: 12px;
        }
        .notify-modal__input {
          flex: 1;
          padding: 12px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          font-size: 14px;
          transition: border-color 0.2s ease;
        }
        .notify-modal__input:focus {
          outline: none;
          border-color: #7b9e89;
        }
        .notify-modal__submit {
          padding: 12px 24px;
          background: #7b9e89;
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .notify-modal__submit:hover {
          background: #6a8d78;
          transform: scale(1.02);
        }
        .notify-modal__submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .notify-modal__privacy {
          font-size: 12px;
          color: #9ca3af;
        }
        @media (max-width: 640px) {
          .notify-modal__content {
            padding: 24px 20px;
          }
          .notify-modal__title {
            font-size: 20px;
          }
          .notify-modal__form {
            flex-direction: column;
          }
        }
      `;
      document.head.appendChild(style);
    }

    // Handle form submission
    const form = modal.querySelector('[data-notify-form]');
    const emailInput = modal.querySelector('[data-notify-email]');
    const submitBtn = modal.querySelector('.notify-modal__submit');
    
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const email = emailInput.value.trim();
      if (!email) return;

      submitBtn.disabled = true;
      submitBtn.textContent = isFr ? 'Envoi...' : 'Sending...';

      try {
        // Save to localStorage for future reference
        const notifications = JSON.parse(localStorage.getItem('sooha_product_notifications') || '[]');
        notifications.push({
          product: productHandle,
          email: email,
          timestamp: Date.now()
        });
        localStorage.setItem('sooha_product_notifications', JSON.stringify(notifications));

        // Show success
        modal.querySelector('.notify-modal__content').innerHTML = `
          <div class="notify-modal__icon">✅</div>
          <h3 class="notify-modal__title">${isFr ? 'Parfait !' : 'Perfect!'}</h3>
          <p class="notify-modal__description">
            ${isFr ? 'Nous vous préviendrons dès que ce produit sera de nouveau disponible.' : 'We\'ll notify you as soon as this product is back in stock.'}
          </p>
          <button class="notify-modal__submit" onclick="this.closest('.notify-modal').remove()">
            ${isFr ? 'Fermer' : 'Close'}
          </button>
        `;

        // Auto-close after 3 seconds
        setTimeout(() => {
          closeModal();
        }, 3000);

      } catch (error) {
        console.error('Failed to save notification:', error);
        submitBtn.disabled = false;
        submitBtn.textContent = isFr ? 'Erreur - Réessayer' : 'Error - Retry';
      }
    });

    // Close handlers
    function closeModal() {
      modal.classList.remove('is-open');
      setTimeout(() => modal.remove(), 300);
    }

    modal.querySelector('.notify-modal__overlay').addEventListener('click', closeModal);
    modal.querySelector('.notify-modal__close').addEventListener('click', closeModal);
    
    document.addEventListener('keydown', function escHandler(e) {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', escHandler);
      }
    });

    // Show modal
    requestAnimationFrame(() => {
      modal.classList.add('is-open');
      emailInput.focus();
    });
  }

  // ============================================
  // Mobile Menu
  // ============================================

  function initMobileMenu() {
    const menu = document.querySelector('[data-mobile-menu]');
    const toggleBtn = document.querySelector('[data-menu-toggle]');
    const closeBtns = document.querySelectorAll('[data-menu-close]');

    if (!menu || !toggleBtn) return;

    const focusTrap = createFocusTrap(menu);
    let previousActiveElement = null;

    function openMenu() {
      previousActiveElement = document.activeElement;
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      toggleBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      document.body.classList.add('menu-open');

      focusTrap.activate();
    }

    function closeMenu() {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      toggleBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      document.body.classList.remove('menu-open');

      focusTrap.deactivate();

      if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
        previousActiveElement.focus();
      } else {
        toggleBtn.focus();
      }
    }

    toggleBtn.addEventListener('click', openMenu);
    closeBtns.forEach(btn => btn.addEventListener('click', closeMenu));

    // Close on overlay click
    menu.addEventListener('click', (e) => {
      if (e.target.matches('[data-menu-close]') || e.target.classList.contains('mobile-menu__overlay')) {
        closeMenu();
      }
    });

    // Expose functions
    window.SOOHA.mobileMenu = { open: openMenu, close: closeMenu };
  }

  // ============================================
  // Cart Drawer
  // ============================================

  function initCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    const toggleBtns = document.querySelectorAll('[data-cart-toggle]');
    const closeBtns = document.querySelectorAll('[data-cart-close]');

    // If drawer exists, cart-drawer.liquid handles all toggle functionality
    // We only need to handle the "page redirect" case when cart_type is NOT drawer
    if (!drawer) {
      // No drawer - redirect to cart page when toggle buttons are clicked
      toggleBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          window.location.href = '/cart';
        });
      });
      return;
    }

    // Drawer exists - cart-drawer.liquid handles toggle buttons via document event delegation
    // We only add focus trap and close button handlers here as backup

    const focusTrap = createFocusTrap(drawer);
    let previousActiveElement = null;

    function openDrawer() {
      previousActiveElement = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.body.classList.add('cart-drawer-open');

      focusTrap.activate();
    }

    function closeDrawer() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.body.classList.remove('cart-drawer-open');

      focusTrap.deactivate();

      if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
        previousActiveElement.focus();
      }
    }

    // Only add close button handlers (toggle is handled by cart-drawer.liquid)
    closeBtns.forEach(btn => btn.addEventListener('click', closeDrawer));

    // Close on overlay click
    drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-close]') || e.target.classList.contains('cart-drawer__overlay')) {
        closeDrawer();
      }
    });

    // Quantity changes using event delegation
    drawer.addEventListener('click', async (e) => {
      const qtyBtn = e.target.closest('[data-qty-change]');
      if (qtyBtn) {
        e.preventDefault();
        const cartItem = qtyBtn.closest('[data-cart-item]');
        if (!cartItem) return;

        // Prevent multiple clicks while updating
        if (cartItem.classList.contains('is-updating')) return;

        const key = cartItem.dataset.key;
        const qtyValue = cartItem.querySelector('[data-qty-value]');
        if (!qtyValue) return;

        const change = parseInt(qtyBtn.dataset.qtyChange, 10);
        const currentQty = parseInt(qtyValue.textContent, 10) || 0;
        const newQty = Math.max(0, currentQty + change);

        // Don't do anything if quantity won't change
        if (newQty === currentQty) return;

        // Disable ALL quantity buttons in this item to prevent race conditions
        cartItem.classList.add('is-updating');
        const allBtns = cartItem.querySelectorAll('[data-qty-change]');
        allBtns.forEach(btn => { btn.disabled = true; });

        // Optimistic UI update
        qtyValue.textContent = newQty;

        try {
          await updateCartItem(key, newQty);
        } finally {
          cartItem.classList.remove('is-updating');
          allBtns.forEach(btn => { btn.disabled = false; });
        }
      }

      const removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        e.preventDefault();
        const cartItem = removeBtn.closest('[data-cart-item]');
        if (!cartItem) return;

        // Prevent multiple clicks
        if (cartItem.classList.contains('is-removing')) return;

        const key = cartItem.dataset.key;
        cartItem.classList.add('is-removing');
        removeBtn.disabled = true;

        try {
          await updateCartItem(key, 0);
        } catch (error) {
          cartItem.classList.remove('is-removing');
          removeBtn.disabled = false;
        }
      }
    });

    // Expose to global
    window.openCartDrawer = openDrawer;
    window.closeCartDrawer = closeDrawer;
  }

  // ============================================
  // Cart Popup
  // ============================================

  function initCartPopup() {
    const popup = document.querySelector('[data-cart-popup]');
    const toggleBtns = document.querySelectorAll('[data-cart-toggle]');

    if (!popup) return;

    const overlay = popup.querySelector('[data-cart-popup-close]');
    const closeBtns = popup.querySelectorAll('[data-cart-popup-close]');
    const focusTrap = createFocusTrap(popup);
    let previousActiveElement = null;

    function openPopup() {
      previousActiveElement = document.activeElement;
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.body.classList.add('cart-popup-open');
      focusTrap.activate();
    }

    function closePopup() {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.body.classList.remove('cart-popup-open');
      focusTrap.deactivate();

      if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
        previousActiveElement.focus();
      }
    }

    toggleBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        if (window.sooha?.cartType !== 'popup') return;
        e.preventDefault();
        openPopup();
      });
    });

    closeBtns.forEach(btn => btn.addEventListener('click', (e) => {
      e.preventDefault();
      closePopup();
    }));

    if (overlay) {
      overlay.addEventListener('click', (e) => {
        e.preventDefault();
        closePopup();
      });
    }

    popup.addEventListener('click', (e) => {
      if (e.target === popup) {
        closePopup();
      }
    });

    // Quantity and remove handlers inside popup
    popup.addEventListener('click', async (e) => {
      const qtyBtn = e.target.closest('[data-qty-change]');
      const removeBtn = e.target.closest('[data-remove-item]');

      if (qtyBtn) {
        e.preventDefault();
        const item = qtyBtn.closest('[data-cart-item]');
        const valueEl = item?.querySelector('[data-qty-value]');
        if (!item || !valueEl) return;

        const key = item.dataset.key;
        const change = parseInt(qtyBtn.dataset.qtyChange, 10) || 0;
        const currentQty = parseInt(valueEl.textContent, 10) || 0;
        const newQty = Math.max(0, currentQty + change);

        if (newQty === currentQty) return;

        qtyBtn.disabled = true;
        valueEl.textContent = newQty;

        try {
          await updateCartItem(key, newQty);
        } finally {
          qtyBtn.disabled = false;
        }
      }

      if (removeBtn) {
        e.preventDefault();
        const item = removeBtn.closest('[data-cart-item]');
        const key = item?.dataset.key;
        if (!key) return;

        removeBtn.disabled = true;
        try {
          await updateCartItem(key, 0);
        } finally {
          removeBtn.disabled = false;
        }
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && popup.classList.contains('is-open')) {
        closePopup();
      }
    });

    document.addEventListener('cart:item-added', () => {
      if (window.sooha?.cartType === 'popup') {
        openPopup();
      }
    });

    window.openCartPopup = openPopup;
    window.closeCartPopup = closePopup;
  }

  // Fallback for cart toggles when drawer/popup not active
  function initCartToggleFallback() {
    document.addEventListener('click', (e) => {
      const cartToggle = e.target.closest('[data-cart-toggle]');
      if (!cartToggle) return;

      if (window.sooha?.cartType === 'drawer' && typeof window.openCartDrawer === 'function') {
        return; // handled by drawer script
      }

      if (window.sooha?.cartType === 'popup' && typeof window.openCartPopup === 'function') {
        e.preventDefault();
        window.openCartPopup();
        return;
      }

      e.preventDefault();
      window.location.href = '/cart';
    });
  }

  // ============================================
  // Search Modal
  // ============================================

  function initSearchModal() {
    const modal = document.querySelector('[data-search-modal]');
    const toggleBtns = document.querySelectorAll('[data-search-toggle]');
    const closeBtns = document.querySelectorAll('[data-search-close]');
    const input = modal?.querySelector('[data-search-input]');
    const results = modal?.querySelector('[data-search-results]');

    if (!modal) return;

    const focusTrap = createFocusTrap(modal);
    let previousActiveElement = null;
    let abortController = null;

    function openModal() {
      previousActiveElement = document.activeElement;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      focusTrap.activate();

      if (input) {
        setTimeout(() => input.focus(), 150);
      }
    }

    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      focusTrap.deactivate();

      if (abortController) {
        abortController.abort();
        abortController = null;
      }

      if (results) results.innerHTML = '';
      if (input) input.value = '';

      if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
        previousActiveElement.focus();
      }
    }

    toggleBtns.forEach(btn => btn.addEventListener('click', openModal));
    closeBtns.forEach(btn => btn.addEventListener('click', closeModal));

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal || e.target.matches('[data-search-close]')) {
        closeModal();
      }
    });

    // Predictive search with AbortController
    if (input && results) {
      const performSearch = debounce(async (query) => {
        if (query.length < 2) {
          results.innerHTML = '';
          return;
        }

        if (abortController) {
          abortController.abort();
        }
        abortController = new AbortController();

        results.innerHTML = '<div class="search-results__loading" aria-live="polite">Searching...</div>';

        try {
          const response = await fetch(
            `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`,
            { signal: abortController.signal }
          );

          if (!response.ok) throw new Error('Search failed');

          const data = await response.json();
          renderSearchResults(data.resources?.results || {}, query);
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error('Search error:', error);
          results.innerHTML = '<p class="search-results__error">Search temporarily unavailable</p>';
        }
      }, 300);

      input.addEventListener('input', (e) => {
        performSearch(e.target.value.trim());
      });

      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && input.value.trim().length >= 2) {
          window.location.href = `/search?q=${encodeURIComponent(input.value.trim())}`;
        }
      });
    }

    function renderSearchResults(data, query) {
      if (!data.products || data.products.length === 0) {
        results.innerHTML = `
          <div class="search-results__empty" aria-live="polite">
            <p>No results found for "${escapeHtml(query)}"</p>
          </div>
        `;
        return;
      }

      const productCards = data.products.map(product => `
        <a href="${product.url}" class="search-results__item">
          <div class="search-results__image">
            ${product.image ? `<img src="${product.image}" alt="" loading="lazy" width="60" height="60">` : ''}
          </div>
          <div class="search-results__info">
            <span class="search-results__title">${escapeHtml(product.title)}</span>
            <span class="search-results__price">${formatMoney(product.price)}</span>
          </div>
        </a>
      `).join('');

      results.innerHTML = `
        <div class="search-results__list" role="listbox" aria-label="Search results">
          ${productCards}
        </div>
        <a href="/search?q=${encodeURIComponent(query)}" class="search-results__view-all">View all results</a>
      `;
    }
  }

  // ============================================
  // Global Escape Key Handler
  // ============================================

  function initGlobalEscapeHandler() {
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;

      // Priority order: search > cart > menu
      const searchModal = document.querySelector('[data-search-modal].is-open');
      if (searchModal) {
        searchModal.classList.remove('is-open');
        searchModal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        return;
      }

      const cartDrawer = document.querySelector('[data-cart-drawer].is-open');
      if (cartDrawer && window.closeCartDrawer) {
        window.closeCartDrawer();
        return;
      }

      const mobileMenu = document.querySelector('[data-mobile-menu].is-open');
      if (mobileMenu && window.SOOHA?.mobileMenu?.close) {
        window.SOOHA.mobileMenu.close();
      }
    });
  }

  // ============================================
  // Cart Operations
  // ============================================

  let cartOperationQueue = Promise.resolve();
  let isCartOperationInProgress = false;

  /**
   * Add item to cart with queue management
   */
  async function addToCart(variantId, quantity = 1) {
    // Validate variant ID before proceeding
    const parsedVariantId = parseInt(variantId, 10);
    if (!variantId || isNaN(parsedVariantId) || parsedVariantId <= 0) {
      console.error('Invalid variant ID:', variantId);
      const error = new Error(document.documentElement.lang === 'fr'
        ? 'Produit non disponible'
        : 'Product not available');
      error.type = 'generic';
      showCartError(error.message, error.type);
      return Promise.reject(error);
    }

    cartOperationQueue = cartOperationQueue.then(async () => {
      if (isCartOperationInProgress) return null;

      isCartOperationInProgress = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{ id: parsedVariantId, quantity: Math.max(1, parseInt(quantity, 10) || 1) }]
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          
          // Parse Shopify error messages for better UX
          let userMessage = errorData.description || errorData.message || 'Failed to add to cart';
          let errorType = 'generic';
          
          // Detect specific error types
          if (userMessage.includes('sold out') || userMessage.includes('not available') || userMessage.includes('out of stock')) {
            errorType = 'out_of_stock';
            userMessage = document.documentElement.lang === 'fr' 
              ? 'Ce produit n\'est plus disponible' 
              : 'This product is no longer available';
          } else if (userMessage.includes('inventory') || userMessage.includes('quantity')) {
            errorType = 'insufficient_stock';
            userMessage = document.documentElement.lang === 'fr'
              ? 'Stock insuffisant pour cette quantité'
              : 'Insufficient stock for this quantity';
          } else if (userMessage.includes('limit')) {
            errorType = 'cart_limit';
            userMessage = document.documentElement.lang === 'fr'
              ? 'Limite de quantité atteinte'
              : 'Cart quantity limit reached';
          }
          
          const error = new Error(userMessage);
          error.type = errorType;
          error.originalData = errorData;
          throw error;
        }

        const data = await response.json();
        await refreshCart();

        // Open cart drawer/popup - wrapped in try-catch to prevent drawer errors from breaking add to cart
        try {
          if (window.openCartDrawer && window.sooha?.cartType === 'drawer') {
            window.openCartDrawer();
          } else if (window.openCartPopup && window.sooha?.cartType === 'popup') {
            window.openCartPopup();
          }
        } catch (drawerError) {
          console.warn('Cart drawer/popup failed to open:', drawerError);
          // Don't throw - the item was successfully added to cart
        }

        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data }));

        return data;
      } catch (error) {
        console.error('Add to cart error:', error);
        
        // Show user-friendly error notification
        showCartError(error.message, error.type || 'generic');
        
        document.dispatchEvent(new CustomEvent('cart:error', { 
          detail: { 
            error: error.message,
            type: error.type,
            variantId: variantId
          } 
        }));
        
        throw error;
      } finally {
        setTimeout(() => {
          isCartOperationInProgress = false;
        }, 400);
      }
    });

    return cartOperationQueue;
  }

  /**
   * Update cart item quantity
   */
  async function updateCartItem(key, quantity) {
    cartOperationQueue = cartOperationQueue.then(async () => {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ id: key, quantity: parseInt(quantity, 10) })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || 'Failed to update cart');
        }

        await refreshCart();
        document.dispatchEvent(new CustomEvent('cart:item-updated', { detail: { key, quantity } }));
      } catch (error) {
        console.error('Update cart error:', error);
        document.dispatchEvent(new CustomEvent('cart:error', { detail: { error: error.message } }));
        // Revert UI on error
        await refreshCart();
      }
    });

    return cartOperationQueue;
  }

  /**
   * Refresh cart state and update UI
   */
  async function refreshCart() {
    try {
      const response = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) throw new Error('Failed to fetch cart');

      const cart = await response.json();

      // Update all cart count badges
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        const count = cart.item_count || 0;

        if (el.classList.contains('header__cart-badge')) {
          el.textContent = count;
          el.classList.toggle('is-hidden', count === 0);

          if (count > 0) {
            el.classList.add('is-updated');
            setTimeout(() => el.classList.remove('is-updated'), 400);
          }
        } else if (el.closest('.cart-drawer__title')) {
          el.textContent = `(${count})`;
        } else {
          el.textContent = count > 0 ? count : '';
        }

        el.dataset.count = count;
      });

      // Update cart total
      document.querySelectorAll('[data-cart-total]').forEach(el => {
        el.textContent = formatMoney(cart.total_price);
      });

      // Refresh cart UI surfaces
      const drawer = document.querySelector('[data-cart-drawer]');
      if (drawer) {
        await refreshCartDrawerContent(drawer);
      }

      const popup = document.querySelector('[data-cart-popup]');
      if (popup) {
        await refreshCartPopupContent(popup);
      }

      await refreshCartPage();
      updateShippingBar(cart);

      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));

      return cart;
    } catch (error) {
      console.error('Refresh cart error:', error);
      return null;
    }
  }

  /**
   * Refresh cart page content via section rendering
   */
  async function refreshCartPage() {
    const cartPage = document.querySelector('.cart-page');
    if (!cartPage) return;

    try {
      const response = await fetch(`${window.Shopify.routes.root}?section_id=cart`);
      if (!response.ok) return;

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newSection = doc.querySelector('.cart-page');

      if (newSection) {
        cartPage.innerHTML = newSection.innerHTML;
      }
    } catch (error) {
      console.error('Cart page refresh error:', error);
    }
  }

  /**
   * Refresh cart drawer content via fetch
   */
  async function refreshCartDrawerContent(drawer) {
    try {
      // Fetch fresh cart-drawer section HTML (safe: Shopify section rendering API)
      const url = (window.Shopify?.routes?.root || '/') + '?section_id=cart-drawer';
      const response = await fetch(url);
      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Replace items and shipping bar
      replaceZone(drawer, doc, '[data-cart-items]');
      replaceZone(drawer, doc, '[data-shipping-bar]');

      // Handle footer (contains totals + Commander button + options)
      const newFooter = doc.querySelector('.cart-drawer__footer');
      const curFooter = drawer.querySelector('.cart-drawer__footer');
      const content = drawer.querySelector('.cart-drawer__content');

      if (newFooter && curFooter) {
        // Both exist — update totals and actions inside the existing footer
        replaceZone(drawer, doc, '.cart-drawer__totals');
        replaceZone(drawer, doc, '.cart-drawer__actions');
      } else if (newFooter && !curFooter && content) {
        // Cart was empty, now has items — append the full footer
        content.appendChild(newFooter.cloneNode(true));
      } else if (!newFooter && curFooter) {
        // Cart is now empty — remove footer
        curFooter.remove();
      }

      // Update count badge inside drawer
      const newCount = doc.querySelector('[data-cart-count]');
      const curCount = drawer.querySelector('[data-cart-count]');
      if (newCount && curCount) curCount.textContent = newCount.textContent;
    } catch (error) {
      console.error('Cart drawer refresh error:', error);
    }
  }

  function replaceZone(container, freshDoc, selector) {
    const newEl = freshDoc.querySelector(selector);
    const curEl = container.querySelector(selector);
    if (newEl && curEl) curEl.innerHTML = newEl.innerHTML;
  }

  /**
   * Refresh cart popup content via section rendering
   */
  async function refreshCartPopupContent(popup) {
    try {
      const response = await fetch(`${window.Shopify.routes.root}?section_id=cart-popup`);

      if (!response.ok) return;

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newContent = doc.querySelector('[data-cart-items]');
      const currentContent = popup.querySelector('[data-cart-items]');
      const newTotal = doc.querySelector('[data-cart-total]');
      const currentTotal = popup.querySelector('[data-cart-total]');
      const newCount = doc.querySelector('[data-cart-count]');
      const currentCount = popup.querySelector('[data-cart-count]');
      const newShipping = doc.querySelector('[data-shipping-bar]');
      const currentShipping = popup.querySelector('[data-shipping-bar]');

      if (newContent && currentContent) {
        currentContent.innerHTML = newContent.innerHTML;
      }

      if (newTotal && currentTotal) {
        currentTotal.textContent = newTotal.textContent;
      }

      if (newCount && currentCount) {
        currentCount.textContent = newCount.textContent;
      }

      if (newShipping && currentShipping) {
        currentShipping.innerHTML = newShipping.innerHTML;
      }
    } catch (error) {
      console.error('Cart popup refresh error:', error);
    }
  }

  /**
   * Update shipping progress bar dynamically
   */
  function updateShippingBar(cart) {
    const threshold = window.sooha?.freeShippingThreshold || 50;
    const thresholdCents = threshold * 100;
    const cartTotal = cart.total_price;
    const remaining = Math.max(0, thresholdCents - cartTotal);
    let progress = thresholdCents > 0 ? Math.round((cartTotal / thresholdCents) * 100) : 0;

    progress = Math.min(100, Math.max(0, progress));

    // Update progress bar width on drawer, page, and popup
    const progressBars = document.querySelectorAll('.cart-drawer__shipping-progress, .cart-page__shipping-progress, .cart-popup__shipping-progress');
    progressBars.forEach(bar => {
      bar.style.width = `${progress}%`;
    });

    // Update shipping texts and success state
    const shippingSections = document.querySelectorAll('[data-shipping-bar]');
    shippingSections.forEach(section => {
      const textEl = section.querySelector('.cart-drawer__shipping-text, .cart-page__shipping-text, .cart-popup__shipping-text');
      if (!textEl) return;

      const successClasses = [
        'cart-drawer__shipping-text--success',
        'cart-page__shipping-text--success',
        'cart-popup__shipping-text--success'
      ];

      if (remaining > 0) {
        const formattedAmount = formatMoney(remaining);
        const locale = window.sooha?.locale;
        let message;
        if (locale === 'fr') {
          message = `Plus que ${formattedAmount} pour profiter de la livraison gratuite !`;
        } else if (locale === 'nl') {
          message = `Nog ${formattedAmount} voor gratis verzending!`;
        } else {
          message = `Only ${formattedAmount} away from free shipping!`;
        }
        textEl.textContent = `🚚 ${message}`;
        successClasses.forEach(cls => textEl.classList.remove(cls));
      } else {
        const locale = window.sooha?.locale;
        let successMessage;
        if (locale === 'fr') {
          successMessage = 'Vous bénéficiez de la livraison gratuite !';
        } else if (locale === 'nl') {
          successMessage = 'U komt in aanmerking voor gratis verzending!';
        } else {
          successMessage = 'You are eligible for free shipping!';
        }
        textEl.textContent = successMessage;
        successClasses.forEach(cls => textEl.classList.add(cls));
      }
    });
  }

  // Listen for external cart:updated events (skip if cart-drawer already refreshed itself)
  document.addEventListener('cart:updated', async (e) => {
    if (e.detail && e.detail._source === 'cart-drawer') return;
    await refreshCart();
  });

  // ============================================
  // Cart Page interactions
  // ============================================

  function initCartPage() {
    const cartPage = document.querySelector('.cart-page');
    if (!cartPage) return;

    const setButtonsDisabled = (wrapper, disabled) => {
      wrapper.querySelectorAll('[data-qty-minus], [data-qty-plus]').forEach(btn => {
        btn.disabled = disabled;
      });
      wrapper.classList.toggle('is-updating', disabled);
    };

    cartPage.addEventListener('click', async (e) => {
      const minusBtn = e.target.closest('[data-qty-minus]');
      const plusBtn = e.target.closest('[data-qty-plus]');
      const removeLink = e.target.closest('.cart-page__item-remove');

      if (minusBtn || plusBtn) {
        e.preventDefault();
        const quantityWrapper = (minusBtn || plusBtn).closest('.cart-page__item-quantity');
        const input = quantityWrapper?.querySelector('.cart-page__qty-input');
        if (!input) return;

        const key = input.dataset.key;
        const currentQty = parseInt(input.value, 10) || 0;
        const delta = plusBtn ? 1 : -1;
        const newQty = Math.max(0, currentQty + delta);

        if (newQty === currentQty) return;

        setButtonsDisabled(quantityWrapper, true);
        input.value = newQty;

        try {
          await updateCartItem(key, newQty);
        } finally {
          setButtonsDisabled(quantityWrapper, false);
        }
        return;
      }

      if (removeLink) {
        e.preventDefault();
        const item = removeLink.closest('.cart-page__item');
        const key = item?.dataset.key;
        if (!key) return;

        removeLink.classList.add('is-loading');
        try {
          await updateCartItem(key, 0);
        } finally {
          removeLink.classList.remove('is-loading');
        }
      }
    });

    cartPage.addEventListener('change', async (e) => {
      const input = e.target.closest('.cart-page__qty-input');
      if (!input) return;

      const key = input.dataset.key;
      const newQty = Math.max(0, parseInt(input.value, 10) || 0);
      input.value = newQty;

      await updateCartItem(key, newQty);
    });
  }

  // ============================================
  // Global Add to Cart Handler
  // ============================================

  function initAddToCartHandlers() {
    // Handle quick-add modal triggers (multi-variant products)
    document.addEventListener('click', (e) => {
      const quickAddBtn = e.target.closest('[data-quick-add]');
      if (!quickAddBtn) return;

      e.preventDefault();
      e.stopPropagation();

      const productHandle = quickAddBtn.dataset.quickAdd;
      if (productHandle && typeof window.openQuickAddModal === 'function') {
        window.openQuickAddModal(productHandle);
      }
    });

    // Handle standalone add-to-cart buttons (not in forms)
    document.addEventListener('click', async (e) => {
      const addBtn = e.target.closest('[data-add-to-cart]');

      if (!addBtn || addBtn.closest('form')) return;

      e.preventDefault();
      e.stopPropagation();

      const variantId = addBtn.dataset.addToCart;

      if (!variantId || addBtn.disabled || addBtn.classList.contains('is-loading')) {
        return;
      }

      addBtn.disabled = true;
      addBtn.classList.add('is-loading');
      addBtn.classList.remove('is-added', 'is-error');

      try {
        await addToCart(variantId, 1);

        addBtn.classList.add('is-added');

        // Haptic feedback on mobile
        if ('vibrate' in navigator) {
          navigator.vibrate(50);
        }

        setTimeout(() => {
          addBtn.classList.remove('is-added');
        }, 2000);
      } catch (error) {
        addBtn.classList.add('is-error');

        setTimeout(() => {
          addBtn.classList.remove('is-error');
        }, 2000);
      } finally {
        addBtn.disabled = false;
        addBtn.classList.remove('is-loading');
      }
    });

    // Handle form submissions for product forms
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-product-form]');
      if (!form) return;

      // Handle both drawer and popup (prevent default form submission)
      if (window.sooha?.cartType !== 'drawer' && window.sooha?.cartType !== 'popup') return;

      e.preventDefault();
      e.stopPropagation();

      const formData = new FormData(form);
      const variantId = formData.get('id');
      const quantity = parseInt(formData.get('quantity'), 10) || 1;

      const submitBtn = form.querySelector('[type="submit"]');

      if (submitBtn?.disabled || submitBtn?.classList.contains('is-loading')) {
        return;
      }

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-loading');
      }

      try {
        await addToCart(variantId, quantity);
      } catch (error) {
        // Error handled in addToCart
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.classList.remove('is-loading');
        }
      }
    });
  }

  // Expose addToCart globally for quick-add modal and external integrations
  window.addToCart = addToCart;

  // ============================================
  // Mobile Optimizations
  // ============================================

  function initMobileOptimizations() {
    // Add touch-device class
    if (isTouchDevice()) {
      document.documentElement.classList.add('touch-device');
    }

    // Fix 100vh issue on mobile browsers
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setViewportHeight();
    window.addEventListener('resize', debounce(setViewportHeight, 100), passiveOption);

    // Set safe area insets
    if (CSS.supports('padding-bottom: env(safe-area-inset-bottom)')) {
      const root = document.documentElement;
      root.style.setProperty('--safe-area-top', 'env(safe-area-inset-top)');
      root.style.setProperty('--safe-area-bottom', 'env(safe-area-inset-bottom)');
      root.style.setProperty('--safe-area-left', 'env(safe-area-inset-left)');
      root.style.setProperty('--safe-area-right', 'env(safe-area-inset-right)');
    }

    // Handle orientation changes
    const handleOrientationChange = debounce(() => {
      document.dispatchEvent(new CustomEvent('orientation:change'));
    }, 100);

    window.addEventListener('orientationchange', handleOrientationChange, passiveOption);
  }

  // ============================================
  // Scroll Lock Utility
  // ============================================

  const scrollLock = {
    scrollPosition: 0,

    enable() {
      this.scrollPosition = window.pageYOffset;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';
    },

    disable() {
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('width');
      window.scrollTo(0, this.scrollPosition);
    }
  };

  window.SOOHA.scrollLock = scrollLock;

  // ============================================
  // Lazy Loading Fallback
  // ============================================

  function initLazyLoading() {
    if ('loading' in HTMLImageElement.prototype) return;

    const lazyImages = document.querySelectorAll('img[loading="lazy"]');
    if (lazyImages.length === 0) return;

    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
          observer.unobserve(img);
        }
      });
    }, {
      rootMargin: '50px 0px',
      threshold: 0.01
    });

    lazyImages.forEach(img => imageObserver.observe(img));
  }

  // ============================================
  // Initialize Everything
  // ============================================

  function init() {
    // Safety net: clear body overflow if no modal/drawer is actually open
    // This prevents scroll getting permanently stuck from interrupted modals
    if (document.body.style.overflow === 'hidden') {
      const anyModalOpen = document.querySelector(
        '.cart-drawer.is-open, [data-cart-popup].is-open, [data-mobile-menu].is-open, .search-modal.is-open, .newsletter-popup[aria-hidden="false"], .notify-modal'
      );
      if (!anyModalOpen) {
        document.body.style.overflow = '';
        document.body.style.removeProperty('position');
        document.body.style.removeProperty('top');
        document.body.style.removeProperty('width');
      }
    }

    initMobileMenu();
    initCartDrawer();
    initCartPopup();
    initSearchModal();
    initGlobalEscapeHandler();
    initAddToCartHandlers();
    initCartPage();
    initCartToggleFallback();
    initProductNotifications();
    initMobileOptimizations();
    initLazyLoading();
    initTouchNavigation();

    // Cart feedback when items are added
    document.addEventListener('cart:item-added', () => {
      // Show success toast for clear visual feedback
      showCartSuccess();

      // Also pulse the cart icon
      const cartIcon = document.querySelector('.header__cart');
      const cartBadge = document.querySelector('.header__cart-badge, .header__cart [data-cart-count]');

      if (cartIcon) {
        cartIcon.classList.remove('is-animating');
        void cartIcon.offsetWidth;
        cartIcon.classList.add('is-animating');
        setTimeout(() => cartIcon.classList.remove('is-animating'), 500);
      }

      if (cartBadge) {
        cartBadge.classList.remove('is-animating');
        void cartBadge.offsetWidth;
        cartBadge.classList.add('is-animating');
        setTimeout(() => cartBadge.classList.remove('is-animating'), 500);
      }
    });

    // Expose cart functions globally
    window.SOOHA.cart = {
      add: addToCart,
      update: updateCartItem,
      refresh: refreshCart
    };

    // Mark as initialized
    document.documentElement.classList.add('js-ready');
  }

  // ============================================
  // Touch Navigation Support
  // For tablet/touch devices: first tap opens dropdown, second tap navigates
  // ============================================
  
  function initTouchNavigation() {
    if (!isTouchDevice()) return;
    
    const navItems = document.querySelectorAll('.header__nav-item.has-dropdown');
    if (!navItems.length) return;
    
    let openDropdown = null;
    
    navItems.forEach(item => {
      const link = item.querySelector('.header__nav-link');
      if (!link) return;
      
      link.addEventListener('click', (e) => {
        const dropdown = item.querySelector('.header__dropdown');
        if (!dropdown) return;
        
        // If this dropdown is already open, let the click navigate
        if (item.classList.contains('is-touch-open')) {
          return; // Allow default navigation
        }
        
        // First tap: prevent navigation and open dropdown
        e.preventDefault();
        
        // Close any other open dropdown
        if (openDropdown && openDropdown !== item) {
          openDropdown.classList.remove('is-touch-open');
        }
        
        // Open this dropdown
        item.classList.add('is-touch-open');
        openDropdown = item;
      });
    });
    
    // Close dropdowns when tapping outside
    document.addEventListener('click', (e) => {
      if (openDropdown && !openDropdown.contains(e.target)) {
        openDropdown.classList.remove('is-touch-open');
        openDropdown = null;
      }
    });
    
    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && openDropdown) {
        openDropdown.classList.remove('is-touch-open');
        openDropdown = null;
      }
    });
  }

  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();